import Modal from "react-modal"
import "./BulkQuestionUpload.style.scss"
import { FileUploader } from "react-drag-drop-files"
import { useEffect, useState } from "react"
import { GoCheckCircleFill } from "react-icons/go"
import Button from "../../component/button/button"
import JSZip from "jszip"
import CustomSelect from "../../component/customSelect/customSelect"
import CustomInput from "../../component/CustomInput/CustomInput"
import { SingleValue } from "react-select"
import { postBulkQuestions } from "../../api/question"
import starAnimation from "../../assets/svg/starAnimation.svg"
import { GRADES } from "../../utils/grade"
import { complexities } from "../../utils/complexity"
import { listSubject } from "src/api/subject"
import {
  getYearValidationError,
  normalizeYearInput,
} from "../../utils/yearValidation"
import LevelSelectors, {
  EMPTY_LEVEL_VALUES,
  LevelFieldErrors,
  LevelFieldNames,
  LevelFieldValues,
} from "../Questions/components/LevelSelectors"
import {
  DEFAULT_LEVEL_LABELS,
  normalizeLabels,
} from "../Subjects/detail/hierarchyUtils"
import {
  hasLevelFieldErrors,
  validateQuestionHierarchyLevels,
} from "../Questions/utils/hierarchyValidation"

const fileTypes = ["ZIP"]

const ALLOWED_PDF_NAMES = new Set([
  "question",
  "answer",
  "solution",
  "marking_scheme",
])

const defaultErrorValue = {
  file: "",
  subject: "",
  levels: {} as LevelFieldErrors,
  grade: "",
  complexity: "",
  year: "",
}

type IBulkQuestionUploadProps = {
  isOpen: boolean
  closeModal: () => void
  notify: () => void
}

type SubjectOption = {
  value: string
  label: string
  level_labels?: string[]
}

const emptySelection = () => ({
  subject: null as SubjectOption | null,
  levels: { ...EMPTY_LEVEL_VALUES } as LevelFieldValues,
  levelNames: {} as LevelFieldNames,
  grade: null as { label: string; value: string } | null,
  complexity: null as { label: string; value: string } | null,
})

const BulkQuestionUpload = ({
  isOpen,
  closeModal,
  notify,
}: IBulkQuestionUploadProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [subjects, setSubjects] = useState<SubjectOption[]>([])
  const [error, setError] = useState({ ...defaultErrorValue })
  const [source, setSource] = useState("")
  const [year, setYear] = useState("")
  const [marks, setMarks] = useState("")
  const [subPartsText, setSubPartsText] = useState("")
  const [selected, setSelected] = useState(emptySelection())

  const validateZipFile = async (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith(".zip")) {
      setError((prev) => ({
        ...prev,
        file: "Only .zip files are supported.",
      }))
      setFile(null)
      return false
    }

    const zip = new JSZip()

    try {
      const zipData = await zip.loadAsync(selectedFile)
      const files = Object.keys(zipData.files).filter(
        (item) => !item.includes("__MACOSX") && !item.endsWith(".DS_Store")
      )

      const rootFolders = files
        .map((path) => path.split("/")[0])
        .filter((name) => name && name !== "__MACOSX")

      const uniqueRootFolders = Array.from(new Set(rootFolders))

      if (uniqueRootFolders.length !== 1) {
        setError((prev) => ({
          ...prev,
          file: "ZIP must contain exactly one root folder.",
        }))
        setFile(null)
        return false
      }

      const parentFolderName = uniqueRootFolders[0]

      const questionFolders = Array.from(
        new Set(
          files
            .filter(
              (path) =>
                path.startsWith(`${parentFolderName}/`) &&
                path.split("/").length === 3
            )
            .map((path) => path.split("/")[1])
            .filter(Boolean)
        )
      )

      if (questionFolders.length === 0) {
        setError((prev) => ({
          ...prev,
          file: "ZIP must contain at least one question folder.",
        }))
        setFile(null)
        return false
      }

      for (const folder of questionFolders) {
        const questionPdf = `${parentFolderName}/${folder}/question.pdf`

        if (!files.includes(questionPdf)) {
          setError((prev) => ({
            ...prev,
            file: `Missing question.pdf in folder "${folder}".`,
          }))
          setFile(null)
          return false
        }
      }

      for (const path of files) {
        if (!path.endsWith(".pdf")) continue

        const pathParts = path.split("/")
        if (pathParts.length < 3) continue

        const fileName = pathParts[2].replace(/\.pdf$/i, "")
        if (!ALLOWED_PDF_NAMES.has(fileName)) {
          setError((prev) => ({
            ...prev,
            file: `Unrecognized file "${pathParts[2]}".`,
          }))
          setFile(null)
          return false
        }
      }

      setError((prev) => ({ ...prev, file: "" }))
      return true
    } catch {
      setError((prev) => ({
        ...prev,
        file: "Could not read the ZIP file.",
      }))
      setFile(null)
      return false
    }
  }

  const handleChange = async (selectedFile: File) => {
    const isValid = await validateZipFile(selectedFile)
    if (isValid) {
      setFile(selectedFile)
    }
  }

  const subjectLabels = normalizeLabels(
    selected.subject?.level_labels && selected.subject.level_labels.length
      ? selected.subject.level_labels
      : DEFAULT_LEVEL_LABELS
  )

  const validateFormData = () => {
    let isValid = true
    const nextErrors = { ...defaultErrorValue, levels: {} as LevelFieldErrors }

    if (!selected.subject?.value) {
      isValid = false
      nextErrors.subject = "Please select a subject."
    } else {
      const levelErrors = validateQuestionHierarchyLevels(
        selected.levels,
        subjectLabels,
        "select"
      )
      if (hasLevelFieldErrors(levelErrors)) {
        isValid = false
        nextErrors.levels = levelErrors
      }
    }
    if (!selected.grade?.value) {
      isValid = false
      nextErrors.grade = "Please select a level."
    }
    if (!selected.complexity?.value) {
      isValid = false
      nextErrors.complexity = "Please select a difficulty."
    }
    if (!file) {
      isValid = false
      nextErrors.file = "Please upload a ZIP file."
    }

    const yearError = getYearValidationError(year)
    if (yearError) {
      isValid = false
      nextErrors.year = yearError
    }

    setError(nextErrors)
    return isValid
  }

  const handleUpload = () => {
    if (!validateFormData() || !file) return

    const formData = new FormData()
    formData.append("subject_id", selected.subject!.value)
    formData.append("level_1_id", selected.levels.level_1)
    if (selected.levels.level_2)
      formData.append("level_2_id", selected.levels.level_2)
    if (selected.levels.level_3)
      formData.append("level_3_id", selected.levels.level_3)
    if (selected.levels.level_4)
      formData.append("level_4_id", selected.levels.level_4)
    formData.append("grade", selected.grade!.value)
    formData.append("complexity", selected.complexity!.value)
    if (source.trim()) formData.append("source", source.trim())
    if (year.trim()) formData.append("year", year.trim())
    if (marks.trim()) formData.append("marks", marks.trim())
    if (subPartsText.trim()) formData.append("sub_parts", subPartsText.trim())
    formData.append("myfile", file)

    setIsUploading(true)
    postBulkQuestions(formData)
      .then(() => {
        resetForm()
        closeModal()
        notify()
      })
      .catch((uploadError) => {
        console.error({ uploadError })
      })
      .finally(() => {
        setIsUploading(false)
      })
  }

  const resetForm = () => {
    setSelected(emptySelection())
    setSource("")
    setYear("")
    setMarks("")
    setSubPartsText("")
    setFile(null)
    setError({ ...defaultErrorValue, levels: {} })
  }

  const handleModelClose = () => {
    if (!isUploading) {
      closeModal()
      resetForm()
    }
  }

  const fetchSubjects = async () => {
    try {
      const res = await listSubject(1, "", 100000)
      const apiSubjects = res.data?.data?.subjects ?? []
      setSubjects(
        apiSubjects.map((subject: {
          id: string
          title?: string
          level_labels?: string[]
        }) => ({
          value: subject.id,
          label: subject.title || "",
          level_labels: Array.isArray(subject.level_labels)
            ? subject.level_labels
            : undefined,
        }))
      )
    } catch (fetchError) {
      console.error("Failed to fetch subjects:", fetchError)
      setSubjects([])
    }
  }

  useEffect(() => {
    if (isOpen && subjects.length === 0) {
      fetchSubjects()
    }
  }, [isOpen, subjects.length])

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleModelClose}
      style={{
        overlay: { zIndex: 1000001 },
        content: { padding: 0, border: "none" },
      }}
      contentLabel='Bulk question upload'
      className='bulk_upload_modal'
      overlayClassName='bulk_upload_modal__overlay'
    >
      {isUploading ? (
        <Loader />
      ) : (
        <div className='bulk_upload_modal__shell'>
          <div className='bulk_upload_modal__header'>
            <h3>Upload bulk questions</h3>
            <p>Defaults apply to every question in the ZIP.</p>
          </div>

          <div className='bulk_upload_modal__body'>
            <div className='bulk_upload_modal__fields'>
              <div className='select'>
                <label htmlFor='bulk-subject'>
                  Subject <span>*</span>
                </label>
                <CustomSelect
                  placeholder='Select subject'
                  id='bulk-subject'
                  data={subjects}
                  onChange={(value: SingleValue<SubjectOption>) => {
                    setSelected((prev) => ({
                      ...prev,
                      subject: value,
                      levels: { ...EMPTY_LEVEL_VALUES },
                      levelNames: {},
                    }))
                    setError((prev) => ({
                      ...prev,
                      subject: "",
                      levels: {},
                    }))
                  }}
                  value={selected.subject}
                  error={error.subject}
                />
              </div>

              {selected.subject?.value && (
                <LevelSelectors
                  subjectId={selected.subject.value}
                  levelLabels={selected.subject.level_labels}
                  values={selected.levels}
                  names={selected.levelNames}
                  onChange={(next, nextNames) => {
                    setSelected((prev) => ({
                      ...prev,
                      levels: next,
                      levelNames: nextNames,
                    }))
                    setError((prev) => ({ ...prev, levels: {} }))
                  }}
                  errors={error.levels}
                  className='select'
                />
              )}

              <div className='select'>
                <label htmlFor='bulk-grade'>
                  Level <span>*</span>
                </label>
                <CustomSelect
                  placeholder='Select level'
                  id='bulk-grade'
                  data={GRADES}
                  onChange={(value: SingleValue<{ label: string; value: string }>) => {
                    setSelected((prev) => ({ ...prev, grade: value }))
                    setError((prev) => ({ ...prev, grade: "" }))
                  }}
                  value={selected.grade}
                  error={error.grade}
                />
              </div>

              <div className='select'>
                <label htmlFor='bulk-complexity'>
                  Difficulty <span>*</span>
                </label>
                <CustomSelect
                  placeholder='Select difficulty'
                  id='bulk-complexity'
                  data={complexities}
                  onChange={(value: SingleValue<{ label: string; value: string }>) => {
                    setSelected((prev) => ({ ...prev, complexity: value }))
                    setError((prev) => ({ ...prev, complexity: "" }))
                  }}
                  value={selected.complexity}
                  error={error.complexity}
                />
              </div>

              <div className='field'>
                <label htmlFor='bulk-subparts'>Sub-parts</label>
                <CustomInput
                  name='subParts'
                  placeholder='e.g. a, b, c'
                  value={subPartsText}
                  onChange={(event) => setSubPartsText(event.target.value)}
                />
              </div>

              <div className='field'>
                <label htmlFor='bulk-source'>Source</label>
                <CustomInput
                  name='source'
                  placeholder='Past paper, textbook'
                  value={source}
                  onChange={(event) => setSource(event.target.value)}
                />
              </div>

              <div className='field'>
                <label htmlFor='bulk-year'>Year</label>
                <CustomInput
                  name='year'
                  placeholder='e.g. 2024'
                  value={year}
                  onChange={(event) => {
                    setYear(normalizeYearInput(event.target.value))
                    setError((prev) => ({ ...prev, year: "" }))
                  }}
                  error={error.year}
                />
              </div>

              <div className='field'>
                <label htmlFor='bulk-marks'>Marks</label>
                <CustomInput
                  name='marks'
                  placeholder='Total marks'
                  value={marks}
                  onChange={(event) => setMarks(event.target.value)}
                />
              </div>
            </div>

            <div className='bulk_upload_modal__hint'>
              <span className='bulk_upload_modal__hint-label'>ZIP format</span>
              <code className='bulk_upload_modal__hint-code'>
                root-folder/question-1/question.pdf
              </code>
              <span className='bulk_upload_modal__hint-note'>
                One root folder, one subfolder per question. Optional: answer.pdf,
                solution.pdf, marking_scheme.pdf
              </span>
            </div>

            <div className='bulk_upload_modal__upload'>
              <label>
                ZIP file <span>*</span>
              </label>
              <FileUploader
                handleChange={handleChange}
                name='file'
                types={fileTypes}
                multiple={false}
              >
                <div className='upload'>
                  {file ? (
                    <>
                      <GoCheckCircleFill
                        color='var(--success-600)'
                        className='check_icon'
                        fontSize={20}
                      />
                      <h3>{file.name}</h3>
                      <p>Click to replace</p>
                    </>
                  ) : (
                    <>
                      <p className='upload__prompt'>Drop ZIP or click to browse</p>
                      <span className='upload__hint'>.zip only</span>
                    </>
                  )}
                </div>
              </FileUploader>
              {error.file && <p className='error_message'>{error.file}</p>}
            </div>
          </div>

          <div className='bulk_upload_modal__footer'>
            <Button
              text='Reset'
              type='link'
              style={{ color: "var(--brand-red-700)" }}
              onClick={resetForm}
            />
            <Button
              text='Process file'
              style={{ height: 40, fontSize: 14 }}
              onClick={handleUpload}
              isLoading={isUploading}
            />
          </div>
        </div>
      )}
    </Modal>
  )
}

export default BulkQuestionUpload

const Loader = () => (
  <div className='upload_loader'>
    <div className='icon_image'>
      <img src={starAnimation} alt='Processing upload' />
    </div>
  </div>
)
